import { TimeReportStatus } from "../enums/TimeReportStatus";

/**
 * The statuses a time report's **rows** may still be written in.
 *
 * A change to a time report's rows is not accepted past
 * `SubmittedByConsultant`, so that hours cannot move under a report something
 * downstream has already acted on. The chain the barrier guards is
 * `Blank → Draft → SubmittedByConsultant → ApprovedByCustomer →
 * ConsultantPaymentProcessed` (the spec calls the last one *paid to
 * co-consultant*); everything from `SubmittedByConsultant` on is either the
 * consultant's own submission or a state a person or a payout has acted on.
 *
 * **Facts are not rows.** `weekComplete` and `customerApprovedFullWeek` are
 * accepted in every status — the barrier stops rows, not facts. If facts were
 * blocked alongside rows, an approval could never reach a submitted report and
 * the booking-kit flow would dead-end at `SubmittedByConsultant`.
 *
 * See ADR-0008 (`0008-time-report-is-a-shared-row-container`) and ADR-0007's
 * terminal-status rule, which this narrows: ERP stops writing at *approved by
 * customer* or *paid out*, and rows stop moving one step earlier still.
 */
export const ROW_WRITABLE_TIME_REPORT_STATUSES: ReadonlySet<TimeReportStatus> = new Set([
  TimeReportStatus.Blank,
  TimeReportStatus.Draft
]);

export function acceptsRowWrites(status: TimeReportStatus | string): boolean {
  return ROW_WRITABLE_TIME_REPORT_STATUSES.has(status as TimeReportStatus);
}

/**
 * Thrown when a row write reaches a report that has moved past `Draft`.
 *
 * Typed rather than a bare `Error` so the barrier reads the same from every
 * writer — the app, a CRM user, the ERP export — and so each transport can map
 * it to its own refusal (423 on the HTTP surface) without string-matching a
 * message. Carries the status it refused, because "which state was it in" is
 * the first question asked about a refused payroll write.
 */
export class TimeReportRowsLockedError extends Error {
  readonly name = "TimeReportRowsLockedError";

  constructor(
    readonly timereportId: string,
    readonly status: TimeReportStatus | string
  ) {
    super(
      `Time report ${timereportId} is ${status}; rows are accepted only in ` +
        `${[...ROW_WRITABLE_TIME_REPORT_STATUSES].join(" and ")}`
    );
  }
}

export function assertAcceptsRowWrites(
  timereportId: string,
  status: TimeReportStatus | string
): void {
  if (!acceptsRowWrites(status)) {
    throw new TimeReportRowsLockedError(timereportId, status);
  }
}
